import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trophy, Trash2, X, Play, CheckCircle2,
  Crown, Users as UsersIcon, Share2, Sparkles, ChevronRight, Upload,
} from 'lucide-react';
import {
  listTournaments, createTournament, getTournament, deleteTournament,
  recordMatchResult, tournamentStatus, tournamentChampion, ROUND_NAMES,
} from '../lib/tournamentEngine';
import { newMatchState, applyPoint, undoPoint, PRESETS } from '../lib/scoringEngine';
import { saveMatch, updateElo } from '../lib/localStore';
import { telemetry } from '../lib/telemetry';
import { publishLocalTournament } from '../lib/syncLayer';
import { ModeToggle } from '../components/ModeToggle';
import BottomNav from '../components/BottomNav';

// ----------------------- Setup -----------------------
function SetupView({ onCreated, onBack }) {
  const [name, setName] = useState('');
  const [presetId, setPresetId] = useState('CLASSIC_21');
  const [playerCount, setPlayerCount] = useState(4);
  const [players, setPlayers] = useState(['', '', '', '']);

  const updateCount = (n) => {
    setPlayerCount(n);
    setPlayers(prev => {
      const arr = [...prev];
      while (arr.length < n) arr.push('');
      arr.length = n;
      return arr;
    });
  };

  const updatePlayer = (i, v) => {
    setPlayers(prev => prev.map((p, idx) => (idx === i ? v : p)));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    try {
      const t = createTournament({ name, players, presetId });
      toast.success(`Tournament "${t.name}" created!`);
      onCreated(t.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom pb-12" data-testid="tournament-setup">
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-9 w-9" data-testid="setup-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-heading text-lg font-extrabold uppercase tracking-tight flex-1">New Tournament</h1>
      </div>

      <form onSubmit={handleCreate} className="max-w-md mx-auto px-4 py-6 space-y-5">
        <div>
          <Label className="text-xs">Tournament Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Smash 2026"
            required
            data-testid="tournament-name"
          />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 block">Match Format</Label>
          <ToggleGroup type="single" value={presetId} onValueChange={(v) => v && setPresetId(v)} className="grid grid-cols-2 gap-2">
            <ToggleGroupItem
              value="CLASSIC_21"
              className="h-12 rounded-xl border-2 data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground flex-col gap-0"
              data-testid="tournament-preset-classic"
            >
              <span className="font-heading font-bold text-sm">Classic 21</span>
              <span className="text-[10px] text-muted-foreground">First to 21</span>
            </ToggleGroupItem>
            <ToggleGroupItem
              value="BWF_3x15"
              className="h-12 rounded-xl border-2 data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:text-foreground flex-col gap-0"
              data-testid="tournament-preset-bwf"
            >
              <span className="font-heading font-bold text-sm">BWF 3x15 Sprint</span>
              <span className="text-[10px] text-muted-foreground">Best-of-3 to 15</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 block">Number of Players</Label>
          <div className="grid grid-cols-4 gap-2">
            {[4, 8, 16, 32].map(n => (
              <Button
                key={n}
                type="button"
                variant={playerCount === n ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateCount(n)}
                className="h-10"
                data-testid={`player-count-${n}`}
              >
                {n}
              </Button>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Leave slots blank for byes — top seeds get auto-advance to round 2.
          </p>
        </div>

        <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Players (in seeding order)</Label>
          {players.map((p, i) => (
            <Input
              key={`player-slot-${i}`}
              value={p}
              onChange={(e) => updatePlayer(i, e.target.value)}
              placeholder={`#${i + 1} — Player name`}
              className="h-10"
              data-testid={`tournament-player-${i}`}
            />
          ))}
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full h-14 rounded-xl font-heading font-extrabold uppercase tracking-wide bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg"
          data-testid="tournament-create-btn"
        >
          <Trophy className="mr-2 h-5 w-5" /> Create Bracket
        </Button>
      </form>
    </div>
  );
}

// ----------------------- List -----------------------
function ListView({ onOpen, onNew, onBack }) {
  const [tournaments, setTournaments] = useState(() => listTournaments());

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this tournament?')) return;
    deleteTournament(id);
    setTournaments(listTournaments());
    toast.success('Tournament deleted');
  };

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom pb-24" data-testid="tournament-list">
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-9 w-9" data-testid="list-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-heading text-xl font-extrabold uppercase tracking-tight flex-1">Tournaments</h1>
        <Button onClick={onNew} size="sm" className="h-9" data-testid="new-tournament-btn">
          <Plus className="h-4 w-4 mr-1" /> New
        </Button>
      </div>

      <div className="flex justify-center pt-4">
        <ModeToggle />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-3">
        {tournaments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Trophy className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                Run a quick knockout tournament with friends. No sign-in needed.
              </p>
              <Button onClick={onNew} data-testid="empty-create-btn">
                <Plus className="h-4 w-4 mr-1" /> Create Your First Tournament
              </Button>
            </CardContent>
          </Card>
        ) : (
          tournaments.map(t => {
            const status = tournamentStatus(t);
            const champion = tournamentChampion(t);
            return (
              <Card
                key={t.id}
                onClick={() => onOpen(t.id)}
                className="cursor-pointer hover:bg-muted/30 transition-colors"
                data-testid={`tournament-card-${t.id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-1 h-12 rounded-full ${status === 'completed' ? 'bg-secondary' : status === 'in_progress' ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-heading font-bold truncate">{t.name}</p>
                      {champion && <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {t.players.length} players • {PRESETS[t.presetId]?.name || 'Match'}
                      {champion && <> • <span className="font-bold text-secondary">{champion}</span></>}
                    </p>
                  </div>
                  <Badge variant={status === 'completed' ? 'default' : 'outline'} className="text-[10px] uppercase">
                    {status === 'completed' ? '🏆 Done' : status === 'in_progress' ? 'Live' : 'New'}
                  </Badge>
                  <button
                    onClick={(e) => handleDelete(e, t.id)}
                    className="text-muted-foreground hover:text-destructive p-1"
                    data-testid={`delete-tournament-${t.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

// ----------------------- Inline Scoring -----------------------
function InlineScoring({ presetId, match, onComplete, onCancel }) {
  const [state, setState] = useState(() =>
    newMatchState({ presetId, matchType: 'singles', sideA: match.sideA, sideB: match.sideB })
  );
  const haptic = (ms = 20) => {
    // Best-effort haptic feedback; silently ignore on unsupported devices.
    try { navigator.vibrate?.(ms); } catch { /* unsupported */ }
  };

  const handleScore = (side) => {
    haptic();
    setState(prev => {
      if (prev.winner) return prev;
      const { state: next, flags } = applyPoint(prev, side);
      if (flags.matchWinner) {
        // Persist match log + ELO + telemetry
        const scoreA = next.games.reduce((s, g) => s + g.scoreA, 0);
        const scoreB = next.games.reduce((s, g) => s + g.scoreB, 0);
        saveMatch({
          id: next.id, presetId: next.presetId, matchType: 'singles',
          sideA: next.sideA, sideB: next.sideB,
          partnerA: '', partnerB: '',
          scoreA, scoreB, games: next.games,
          winner: next.winner, startedAt: next.startedAt,
          completedAt: next.completedAt, durationSec: next.durationSec,
        });
        const winnerName = next.winner === 'A' ? next.sideA : next.sideB;
        const loserName = next.winner === 'A' ? next.sideB : next.sideA;
        updateElo(winnerName, loserName);
        telemetry.matchCompleted({
          presetId: next.presetId, durationSec: next.durationSec,
          sideA: next.sideA, sideB: next.sideB, winner: next.winner,
        });
        const scoreLine = next.games.map(g => `${g.scoreA}-${g.scoreB}`).join(', ');
        setTimeout(() => onComplete({ winner: next.winner, scoreLine }), 600);
      }
      return next;
    });
  };

  const handleUndo = (side) => {
    haptic(15);
    setState(prev => undoPoint(prev, side));
  };

  const game = state.games[state.currentGameIdx];
  const isCompleted = !!state.winner;

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="tournament-inline-scoring">
        <div className="bg-card border-b border-border px-4 py-2 flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {ROUND_NAMES(99, match.roundIdx)} • Match {match.slotIdx + 1}
          </p>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" data-testid="inline-cancel">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => !isCompleted && handleScore('A')}
              disabled={isCompleted}
              className="rounded-xl bg-primary/10 border-2 border-primary/30 active:bg-primary/25 active:scale-[0.98] transition-all flex flex-col items-center justify-center py-6 disabled:opacity-50"
              data-testid="inline-score-left"
            >
              <p className="text-xs font-heading font-bold uppercase text-primary truncate max-w-full">{state.sideA}</p>
              <p className="font-heading text-6xl font-black text-primary tabular-nums">{game.scoreA}</p>
              <p className="text-[10px] text-muted-foreground">Tap +1</p>
            </button>
            <button
              onClick={() => !isCompleted && handleScore('B')}
              disabled={isCompleted}
              className="rounded-xl bg-secondary/10 border-2 border-secondary/30 active:bg-secondary/25 active:scale-[0.98] transition-all flex flex-col items-center justify-center py-6 disabled:opacity-50"
              data-testid="inline-score-right"
            >
              <p className="text-xs font-heading font-bold uppercase text-secondary truncate max-w-full">{state.sideB}</p>
              <p className="font-heading text-6xl font-black text-secondary tabular-nums">{game.scoreB}</p>
              <p className="text-[10px] text-muted-foreground">Tap +1</p>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" size="sm" onClick={() => handleUndo('A')} className="h-8" data-testid="inline-undo-left">Undo</Button>
            <Button variant="ghost" size="sm" onClick={() => handleUndo('B')} className="h-8" data-testid="inline-undo-right">Undo</Button>
          </div>
          {state.games.length > 1 && (
            <div className="flex justify-center gap-2 flex-wrap">
              {state.games.map((g, i) => (
                <Badge key={`inline-set-${i}`} variant={i === state.currentGameIdx ? 'default' : 'outline'} className="font-mono text-[11px]">
                  S{i + 1}: {g.scoreA}-{g.scoreB}{g.winner ? ` ✓${g.winner}` : ''}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------- Champion Modal -----------------------
function ChampionModal({ name, tournamentName, onClose }) {
  const handleShare = () => {
    const text = `🏆 ${name} wins ${tournamentName}!\n\nScored on LoveAll — try it free.`;
    if (navigator.share) {
      navigator.share({ title: 'Tournament Champion', text }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-0 overflow-hidden gap-0" data-testid="champion-modal">
        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-secondary p-6 text-center space-y-2">
          <Crown className="h-14 w-14 mx-auto text-white drop-shadow-lg" />
          <p className="text-white/90 text-xs uppercase tracking-widest">Champion</p>
          <h2 className="font-heading text-3xl font-extrabold text-white uppercase tracking-tight">{name}</h2>
          <p className="text-white/90 text-sm">{tournamentName}</p>
        </div>
        <div className="p-5 space-y-3">
          <Button onClick={handleShare} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="champion-share">
            <Share2 className="mr-2 h-4 w-4" /> Share to WhatsApp
          </Button>
          <Button onClick={onClose} variant="outline" className="w-full" data-testid="champion-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------- Detail / Bracket -----------------------
function DetailView({ tournamentId, onBack }) {
  const [t, setT] = useState(() => getTournament(tournamentId));
  const [scoringMatch, setScoringMatch] = useState(null);
  const [champion, setChampion] = useState(null);

  const refresh = useCallback(() => setT(getTournament(tournamentId)), [tournamentId]);

  useEffect(() => {
    if (t?.completedAt && !champion) {
      setChampion(tournamentChampion(t));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t?.completedAt]);

  if (!t) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-sm">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground">Tournament not found.</p>
            <Button onClick={onBack} className="mt-3">Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleMatchClick = (match) => {
    if (!match.sideA || !match.sideB || match.winner) return;
    setScoringMatch(match);
  };

  const handleScoringComplete = ({ winner, scoreLine }) => {
    const { isComplete, championName } = recordMatchResult(t.id, scoringMatch.id, { winner, scoreLine });
    setScoringMatch(null);
    refresh();
    if (isComplete && championName) {
      setChampion(championName);
    } else {
      toast.success(`Match locked: ${scoreLine}`);
    }
  };

  const status = tournamentStatus(t);

  const handlePublish = async () => {
    if (!window.confirm(`Publish "${t.name}" to the public tournament list? Anyone with the link will be able to view it.`)) return;
    const res = await publishLocalTournament(t, { organizer_name: 'Local Tournament' });
    if (res?.ok) {
      const url = `${window.location.origin}/tournaments/${res.tournament_id}`;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
      toast.success('Published! Public link copied to clipboard.', { duration: 4000 });
    } else {
      toast.error(res?.error || 'Failed to publish');
    }
  };

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom pb-24" data-testid="tournament-detail">
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-9 w-9" data-testid="detail-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-base font-extrabold uppercase tracking-tight truncate">{t.name}</h1>
          <p className="text-[10px] text-muted-foreground">
            {t.players.length} players • {PRESETS[t.presetId]?.name}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePublish}
          className="h-8 px-2 text-[11px] gap-1"
          data-testid="publish-local-btn"
          title="Publish to public tournament list"
        >
          <Upload className="h-3.5 w-3.5" />
          <span className="hidden xs:inline sm:inline">Publish</span>
        </Button>
        <Badge variant={status === 'completed' ? 'default' : 'outline'} className="text-[10px] uppercase">
          {status === 'completed' ? '🏆 Done' : status === 'in_progress' ? 'Live' : 'New'}
        </Badge>
      </div>

      {/* Bracket — horizontal scroll on mobile */}
      <div className="overflow-x-auto">
        <div className="inline-flex gap-3 p-4 min-w-full">
          {t.rounds.map((round, roundIdx) => (
            <div key={`round-${roundIdx}`} className="flex flex-col gap-3 min-w-[180px] sm:min-w-[210px]" data-testid={`round-${roundIdx}`}>
              <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-center">
                {ROUND_NAMES(t.rounds.length, roundIdx)}
              </h3>
              <div className="flex flex-col justify-around flex-1 gap-3">
                {round.map((m, slotIdx) => {
                  const playable = m.sideA && m.sideB && !m.winner;
                  const completed = !!m.winner;
                  const isBye = m.scoreLine === 'BYE';
                  return (
                    <button
                      key={m.id}
                      onClick={() => handleMatchClick(m)}
                      disabled={!playable}
                      className={`text-left rounded-lg border p-2 transition-all ${
                        completed
                          ? 'border-secondary/40 bg-secondary/5'
                          : playable
                            ? 'border-primary/40 bg-primary/5 hover:bg-primary/10 active:scale-[0.98] cursor-pointer'
                            : 'border-border bg-muted/20 cursor-not-allowed opacity-70'
                      }`}
                      data-testid={`match-${m.id}`}
                    >
                      <div className={`text-xs py-1 flex items-center justify-between ${m.winner === 'A' ? 'font-bold text-primary' : ''}`}>
                        <span className="truncate">{m.sideA || <em className="text-muted-foreground/60">—</em>}</span>
                        {m.winner === 'A' && <CheckCircle2 className="h-3.5 w-3.5 ml-1 shrink-0" />}
                      </div>
                      <div className="border-t border-border my-0.5" />
                      <div className={`text-xs py-1 flex items-center justify-between ${m.winner === 'B' ? 'font-bold text-secondary' : ''}`}>
                        <span className="truncate">{m.sideB || <em className="text-muted-foreground/60">—</em>}</span>
                        {m.winner === 'B' && <CheckCircle2 className="h-3.5 w-3.5 ml-1 shrink-0" />}
                      </div>
                      <div className="mt-1.5 text-[10px] text-center">
                        {isBye ? (
                          <Badge variant="outline" className="text-[9px] px-1.5">BYE</Badge>
                        ) : completed ? (
                          <span className="font-mono text-muted-foreground">{m.scoreLine}</span>
                        ) : playable ? (
                          <span className="text-primary flex items-center justify-center gap-1 font-bold">
                            <Play className="h-3 w-3" /> Score
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60">Waiting…</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inline scoring */}
      {scoringMatch && (
        <InlineScoring
          presetId={t.presetId}
          match={scoringMatch}
          onComplete={handleScoringComplete}
          onCancel={() => setScoringMatch(null)}
        />
      )}

      {/* Champion celebration */}
      {champion && (
        <ChampionModal
          name={champion}
          tournamentName={t.name}
          onClose={() => setChampion(null)}
        />
      )}
    </div>
  );
}

// ----------------------- Page Root -----------------------
export default function LocalTournament() {
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // 'list' | 'setup' | 'detail'
  const [activeId, setActiveId] = useState(null);

  const openDetail = (id) => { setActiveId(id); setView('detail'); };
  const openSetup = () => setView('setup');
  const openList = () => { setActiveId(null); setView('list'); };

  if (view === 'setup') return <SetupView onCreated={openDetail} onBack={openList} />;
  if (view === 'detail' && activeId) return <DetailView tournamentId={activeId} onBack={openList} />;
  return (
    <>
      <ListView onOpen={openDetail} onNew={openSetup} onBack={() => navigate('/')} />
      <BottomNav />
    </>
  );
}
